import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { handleFeatureNotIncluded } from "../../utils/notifyPlanUpgrade";
import axios from "axios";
import { useAuth } from "../../auth/AuthContext";
import {
  getProgram,
  saveProgram as saveProgramApi,
  getCards,
  getMetrics,
} from "../../services/loyaltyService";
import LoyaltyMetrics from "./components/LoyaltyMetrics";
import ProgramSetup from "./components/ProgramSetup";
import LoyaltyCardItem from "./components/LoyaltyCardItem";
import styles from "./Fidelidade.module.css";

/**
 * Tela de Programa de Fidelidade — exclusiva para tenants do segmento
 * "barbearias". Estrutura em duas abas:
 *
 * 1. Meu Programa  — métricas + configuração (ProgramSetup + LoyaltyMetrics)
 * 2. Meus Clientes — lista paginada de cartões (LoyaltyCardItem)
 *
 * Gating de segmento:
 * - Item de menu lateral é filtrado no `Agenda.js` antes de chegar aqui.
 * - A rota também valida `user.tenant?.segment === "barbearias"`; caso
 *   contrário, redireciona pra /dashboard com toast informativo (UX
 *   defensiva contra acesso direto via URL).
 *
 * Backend é a segunda linha de defesa: todas as rotas de /loyalty/* retornam
 * 403 quando o tenant não é barbearia (loyaltyRoutes.ts).
 */

const SEGMENT_BARBEARIAS = "barbearias";
const PAGE_SIZE = 20;
// busca em memória, debounce só pra evitar re-render por tecla
const SEARCH_DEBOUNCE_MS = 100;

/** Detecta se um erro do axios é cancelamento (AbortController.abort) — no-op. */
function isCanceledError(err) {
  return (
    axios.isCancel?.(err) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError"
  );
}

const TABS = {
  PROGRAM: "program",
  CARDS: "cards",
};

export default function Fidelidade() {
  const { user } = useAuth();
  const segment = user?.tenant?.segment ?? null;
  const isBarbearia = segment === SEGMENT_BARBEARIAS;

  // ─── Gating: tenants não-barbearia veem toast e são redirecionados ────────
  // Usamos um ref pra disparar o toast só uma vez no mount do componente,
  // independente de re-renders do AuthContext.
  const toastShownRef = useRef(false);
  useEffect(() => {
    if (user !== null && !isBarbearia && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.info(
        "Esta funcionalidade está disponível apenas para barbearias.",
      );
    }
  }, [user, isBarbearia]);

  // Aguarda o auth context popular o user (evita redirect prematuro
  // antes do `getProfile` async retornar no mount inicial).
  if (user === null) {
    return null;
  }

  if (!isBarbearia) {
    return <Navigate to="/dashboard" replace />;
  }

  return <FidelidadeContent />;
}

// ─── Conteúdo real (separado para evitar early-return + hooks) ──────────────
function FidelidadeContent() {
  const [activeTab, setActiveTab] = useState(TABS.PROGRAM);

  // ─── Estado do programa ─────────────────────────────────────────
  const [program, setProgram] = useState(null);
  const [programStatus, setProgramStatus] = useState("loading"); // loading | ready | error | not_configured
  const [savingProgram, setSavingProgram] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [showProgramForm, setShowProgramForm] = useState(false);

  // ─── Estado das métricas ────────────────────────────────────────
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // ─── Estado da lista ────────────────────────────────────────────
  const [cards, setCards] = useState([]);
  const [cardsTotal, setCardsTotal] = useState(0);
  const [cardsPage, setCardsPage] = useState(1);
  const [cardsStatus, setCardsStatus] = useState("loading"); // loading | ready | error
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState("all"); // all | nearReward
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef(null);
  const cardsAbortRef = useRef(null);

  // ─── Fetch programa ─────────────────────────────────────────────
  const fetchProgram = useCallback(async () => {
    setProgramStatus("loading");
    try {
      const res = await getProgram();
      setProgram(res.data);
      setShowProgramForm(false);
      setProgramStatus("ready");
    } catch (err) {
      if (err?.response?.status === 404) {
        setProgram(null);
        setProgramStatus("not_configured");
      } else {
        setProgramStatus("error");
        toast.error("Não foi possível carregar o programa de fidelidade.");
      }
    }
  }, []);

  // ─── Fetch métricas ─────────────────────────────────────────────
  const fetchMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const res = await getMetrics();
      setMetrics(res.data);
    } catch (err) {
      // 404 = programa não configurado → métricas zeradas (não é erro real).
      if (err?.response?.status === 404) {
        setMetrics({
          totalCards: 0,
          totalRewardsGiven: 0,
          nearRewardCount: 0,
        });
      } else {
        // Falha silenciosa nas métricas — não bloqueia o resto da tela.
        setMetrics(null);
      }
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  // ─── Fetch cartões (com paginação e AbortController) ────────────
  const fetchCards = useCallback(
    async ({ page = 1, append = false, nearReward = false } = {}) => {
      // Cancela request anterior em flight pra evitar race condition
      cardsAbortRef.current?.abort();
      const controller = new AbortController();
      cardsAbortRef.current = controller;

      if (append) {
        setLoadingMore(true);
      } else {
        setCardsStatus("loading");
      }

      try {
        const res = await getCards(
          {
            page,
            limit: PAGE_SIZE,
            ...(nearReward ? { nearReward: true } : {}),
          },
          { signal: controller.signal },
        );
        const data = res?.data?.data ?? [];
        const total = res?.data?.total ?? 0;
        setCards((prev) => (append ? [...prev, ...data] : data));
        setCardsTotal(total);
        setCardsPage(page);
        setCardsStatus("ready");
      } catch (err) {
        if (isCanceledError(err)) return;
        setCardsStatus("error");
        if (!append) {
          toast.error("Não foi possível carregar os clientes.");
        }
      } finally {
        if (cardsAbortRef.current === controller) {
          setLoadingMore(false);
        }
      }
    },
    [],
  );

  // ─── Mount: dispara programa + métricas + cards ──────────────────
  useEffect(() => {
    fetchProgram();
    fetchMetrics();
    fetchCards({ page: 1, append: false, nearReward: false });
    return () => {
      cardsAbortRef.current?.abort();
    };
  }, [fetchProgram, fetchMetrics, fetchCards]);

  // ─── Debounce da busca (100ms — busca é local, mas evita re-render
  // a cada tecla quando a lista é grande) ──────────────────────────
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [search]);

  // ─── Salvar programa (POST upsert) ───────────────────────────────
  const handleSaveProgram = useCallback(
    async (payload) => {
      setSavingProgram(true);
      try {
        const res = await saveProgramApi(payload);
        setProgram(res.data);
        setProgramStatus("ready");
        setShowProgramForm(false);
        toast.success(
          payload.isActive
            ? "Programa salvo! Seu chatbot já está contando as visitas. 🎉"
            : "Programa salvo, mas ainda inativo. Ative-o para começar a contar as visitas.",
        );
        // Atualiza métricas e recarrega cartões — o requiredVisits pode ter
        // mudado e isso afeta o cálculo do "Quase lá!" e da barra.
        fetchMetrics();
        fetchCards({
          page: 1,
          append: false,
          nearReward: filter === "nearReward",
        });
      } catch (err) {
        // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): fidelidade
        // acumula por atendimento realizado, então as rotas de escrita do
        // programa são gateadas. Convite de upgrade, nunca o texto cru.
        if (handleFeatureNotIncluded(err)) return;
        const apiMsg = err?.response?.data?.error;
        if (typeof apiMsg === "string") {
          toast.error(apiMsg);
        } else {
          toast.error(
            "Não foi possível salvar o programa. Tente novamente em instantes.",
          );
        }
      } finally {
        setSavingProgram(false);
      }
    },
    [fetchMetrics, fetchCards, filter],
  );

  // ─── Toggle ativo inline (PATCH imediato) ────────────────────────
  // Diferente do "Salvar programa": persiste só a flag `isActive` na hora,
  // usando os valores PERSISTIDOS de visits/reward (não os edits locais do
  // formulário). Optimistic update + rollback em erro. Trava reentrância com
  // a flag `togglingActive` (também passada ao ProgramSetup pra desabilitar o
  // toggle enquanto o request está em flight).
  const handleToggleActive = useCallback(
    async (nextValue) => {
      if (togglingActive) return;
      const current = program;
      // Sem programa persistido não há o que PATCHar (proteção defensiva —
      // em create mode o toggle nem chama este handler).
      if (!current) return;

      setTogglingActive(true);
      // Optimistic: vira visualmente na hora.
      setProgram((prev) => (prev ? { ...prev, isActive: nextValue } : prev));

      try {
        const res = await saveProgramApi({
          requiredVisits: current.requiredVisits,
          rewardDescription: current.rewardDescription,
          isActive: nextValue,
        });
        // Reconcilia com a resposta autoritativa do backend.
        setProgram(res.data);
        toast.success(
          nextValue
            ? "Programa ativado! Seu chatbot já começa a contar as visitas. 🎉"
            : "Programa pausado. As visitas não serão mais contadas.",
        );
        // O número de visitas não mudou, mas as métricas trazem `programActive`
        // e o estado pode influenciar contadores derivados — re-sincroniza.
        fetchMetrics();
      } catch (err) {
        // Rollback pro estado anterior ao optimistic update.
        setProgram(current);
        // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): fidelidade
        // acumula por atendimento realizado, então as rotas de escrita do
        // programa são gateadas. Convite de upgrade, nunca o texto cru.
        if (handleFeatureNotIncluded(err)) return;
        const apiMsg = err?.response?.data?.error;
        if (typeof apiMsg === "string") {
          toast.error(apiMsg);
        } else {
          toast.error(
            "Não foi possível atualizar o programa. Tente novamente em instantes.",
          );
        }
      } finally {
        setTogglingActive(false);
      }
    },
    [program, togglingActive, fetchMetrics],
  );

  // ─── Trocar filtro Todos/Quase lá! ────────────────────────────────
  const handleFilterChange = useCallback(
    (next) => {
      if (next === filter) return;
      setFilter(next);
      setCardsPage(1);
      fetchCards({
        page: 1,
        append: false,
        nearReward: next === "nearReward",
      });
    },
    [filter, fetchCards],
  );

  // ─── Carregar mais ────────────────────────────────────────────────
  const handleLoadMore = useCallback(() => {
    fetchCards({
      page: cardsPage + 1,
      append: true,
      nearReward: filter === "nearReward",
    });
  }, [cardsPage, fetchCards, filter]);

  // ─── Busca local (sobre cards já carregados) ──────────────────────
  const filteredCards = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((card) => {
      const name = (card.clientName || "").toLowerCase();
      const phoneDigits = (card.clientPhone || "").replace(/\D/g, "");
      const qDigits = q.replace(/\D/g, "");
      return (
        name.includes(q) ||
        (qDigits.length > 0 && phoneDigits.includes(qDigits))
      );
    });
  }, [cards, debouncedSearch]);

  const hasMore = cards.length < cardsTotal;
  const isActive = program?.isActive === true;
  const requiredVisits = program?.requiredVisits ?? 10;

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <span aria-hidden="true">🏆</span> Fidelidade
          </h1>
          <p className={styles.subtitle}>
            Configure seu programa de cartão fidelidade e veja quais clientes
            estão próximos de ganhar a recompensa.
          </p>
        </div>

        {program && (
          <span
            className={`${styles.statusBadge} ${
              isActive ? styles.statusActive : styles.statusInactive
            }`}
            aria-label={isActive ? "Programa ativo" : "Programa inativo"}
          >
            <span className={styles.statusDot} aria-hidden="true" />
            {isActive ? "Ativo" : "Inativo"}
          </span>
        )}
      </header>

      {/* ─── Tabs ───────────────────────────────────────────────────── */}
      <nav className={styles.tabs} role="tablist" aria-label="Seções de Fidelidade">
        <button
          type="button"
          role="tab"
          id="tab-program"
          aria-selected={activeTab === TABS.PROGRAM}
          aria-controls="panel-program"
          className={`${styles.tab} ${activeTab === TABS.PROGRAM ? styles.tabActive : ""}`.trim()}
          onClick={() => setActiveTab(TABS.PROGRAM)}
        >
          Meu Programa
        </button>
        <button
          type="button"
          role="tab"
          id="tab-cards"
          aria-selected={activeTab === TABS.CARDS}
          aria-controls="panel-cards"
          className={`${styles.tab} ${activeTab === TABS.CARDS ? styles.tabActive : ""}`.trim()}
          onClick={() => setActiveTab(TABS.CARDS)}
        >
          Meus Clientes
        </button>
      </nav>

      {/* ─── Painel: Meu Programa ───────────────────────────────────── */}
      {activeTab === TABS.PROGRAM && (
        <section
          id="panel-program"
          role="tabpanel"
          aria-labelledby="tab-program"
        >
          {programStatus === "loading" && (
            <>
              <LoyaltyMetrics loading />
              <div className={styles.skeletonItem} style={{ height: 320 }} />
            </>
          )}

          {programStatus === "error" && (
            <div className={styles.errorBox} role="alert">
              <span>
                Não foi possível carregar a configuração do programa.
              </span>
              <button
                type="button"
                className={styles.retryBtn}
                onClick={fetchProgram}
              >
                Tentar novamente
              </button>
            </div>
          )}

          {programStatus === "not_configured" && !showProgramForm && (
            <EmptyProgramState onCreate={() => setShowProgramForm(true)} />
          )}

          {programStatus === "not_configured" && showProgramForm && (
            <ProgramSetup
              program={null}
              onSave={handleSaveProgram}
              saving={savingProgram}
            />
          )}

          {programStatus === "ready" && program && (
            <>
              <LoyaltyMetrics metrics={metrics} loading={metricsLoading} />
              <ProgramSetup
                program={program}
                onSave={handleSaveProgram}
                onToggleActive={handleToggleActive}
                saving={savingProgram}
                togglingActive={togglingActive}
              />
            </>
          )}
        </section>
      )}

      {/* ─── Painel: Meus Clientes ──────────────────────────────────── */}
      {activeTab === TABS.CARDS && (
        <section
          id="panel-cards"
          role="tabpanel"
          aria-labelledby="tab-cards"
        >
          <div className={styles.listToolbar}>
            <div className={styles.searchWrap}>
              <span className={styles.searchIcon} aria-hidden="true">
                🔎
              </span>
              <input
                type="search"
                className={styles.searchInput}
                placeholder="Buscar por nome ou telefone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Buscar cliente por nome ou telefone"
              />
            </div>
            <div className={styles.chips} role="group" aria-label="Filtro de cartões">
              <button
                type="button"
                className={`${styles.chip} ${filter === "all" ? styles.chipActive : ""}`.trim()}
                onClick={() => handleFilterChange("all")}
                aria-pressed={filter === "all"}
              >
                Todos
              </button>
              <button
                type="button"
                className={`${styles.chip} ${filter === "nearReward" ? styles.chipActive : ""}`.trim()}
                onClick={() => handleFilterChange("nearReward")}
                aria-pressed={filter === "nearReward"}
              >
                ⭐ Quase lá!
              </button>
            </div>
          </div>

          {cardsStatus === "loading" && (
            <ul className={styles.list} aria-busy="true" aria-label="Carregando clientes">
              {[0, 1, 2, 3].map((i) => (
                <li key={i} className={styles.skeletonItem} aria-hidden="true" />
              ))}
            </ul>
          )}

          {cardsStatus === "error" && (
            <div className={styles.errorBox} role="alert">
              <span>Não foi possível carregar os clientes.</span>
              <button
                type="button"
                className={styles.retryBtn}
                onClick={() =>
                  fetchCards({
                    page: 1,
                    append: false,
                    nearReward: filter === "nearReward",
                  })
                }
              >
                Tentar novamente
              </button>
            </div>
          )}

          {cardsStatus === "ready" && cards.length === 0 && (
            <EmptyCardsState filter={filter} />
          )}

          {cardsStatus === "ready" && cards.length > 0 && filteredCards.length === 0 && (
            <div className={styles.emptyState} role="status">
              <span className={styles.emptyIcon} aria-hidden="true">🔎</span>
              <h2 className={styles.emptyTitle}>Nenhum cliente encontrado</h2>
              <p className={styles.emptyText}>
                Tente buscar por outro nome ou telefone, ou limpe a busca para
                ver todos os clientes do filtro atual.
              </p>
            </div>
          )}

          {cardsStatus === "ready" && filteredCards.length > 0 && (
            <ul className={styles.list}>
              {filteredCards.map((card) => (
                <LoyaltyCardItem
                  key={card.id}
                  card={card}
                  requiredVisits={requiredVisits}
                />
              ))}
            </ul>
          )}

          {cardsStatus === "ready" && hasMore && !debouncedSearch.trim() && (
            <div className={styles.loadMoreWrap}>
              <button
                type="button"
                className={styles.loadMoreBtn}
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Carregando..." : "Carregar mais"}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ─── Empty states (extraídos pra manter o componente principal legível) ─────

function EmptyProgramState({ onCreate }) {
  return (
    <div
      role="region"
      aria-label="Programa de fidelidade ainda não configurado"
      className={styles.emptyProgram}
    >
      <span className={styles.emptyProgramIcon} aria-hidden="true">
        🎴
      </span>
      <h2 className={styles.emptyProgramTitle}>
        Você ainda não tem um programa de fidelidade
      </h2>
      <p className={styles.emptyProgramText}>
        Com o programa de fidelidade, seus clientes ganham um prêmio a cada
        X cortes — e voltam mais vezes para completar o cartão.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className={styles.emptyProgramCta}
      >
        ✨ Criar meu programa de fidelidade
      </button>
    </div>
  );
}

function EmptyCardsState({ filter }) {
  const isFiltered = filter === "nearReward";
  return (
    <div className={styles.emptyState} role="status">
      <span className={styles.emptyIcon} aria-hidden="true">
        {isFiltered ? "⭐" : "🎴"}
      </span>
      <h2 className={styles.emptyTitle}>
        {isFiltered
          ? "Nenhum cliente próximo do prêmio"
          : "Nenhum cliente no programa ainda"}
      </h2>
      <p className={styles.emptyText}>
        {isFiltered
          ? "Quando algum cliente estiver a uma visita de ganhar o prêmio, ele aparece aqui."
          : "Quando seus clientes agendarem pelo WhatsApp, eles entrarão automaticamente no cartão fidelidade."}
      </p>
    </div>
  );
}
